import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import type { IssueScope } from "./issue-scope";
import { scopeWorkflowId } from "./issue-scope";
import type { QuickFilterDef } from "./types";

/**
 * The quick-filter list a screen's SCOPE has already narrowed.
 *
 * A screen pinned to one workflow answers "which workflow?" before the panel
 * opens — every row it can show carries that id — so offering the choice is a
 * control whose only useful setting is the one already in force, and whose
 * every other setting returns nothing. Stages are the opposite: they are the
 * question a workflow screen most wants asked, so the composite keeps its
 * stage half and loses only the level the scope fixed.
 *
 * Nothing is dropped for the other scopes, and that is not an oversight. A
 * team screen matches a project assigned to that team OR worked by its
 * members, so a project can carry other teams and filtering by one narrows
 * further; a member screen's projects can carry co-assignees. Only a workflow
 * is one-per-project, and only a value the scope pins to exactly one is dead.
 */
export function scopeQuickFilterDefs(
  defs: QuickFilterDef[],
  scope: IssueScope | undefined,
): QuickFilterDef[] {
  const workflowId = scope ? scopeWorkflowId(scope) : undefined;
  // Same reference when nothing is scoped away — callers memoize on it.
  if (!workflowId) return defs;
  return defs.map((def) =>
    def.type === "workflow-stage"
      ? {
          ...def,
          label: "Stage",
          icon: PROPERTY_ICON.stage,
          unfilteredFace: "Any stage",
        }
      : def,
  );
}
